import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CommonModule } from '@angular/common';
import { AdminService } from '../../../services/admin.service';
import { PostUser, UpdateUser, User } from '../../../interfaces/admin/Users';
import { ServerResponse, ServerResponseObject } from '../../../interfaces/ServerResponse';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

interface AddUserDialogData {
  user?: User;
}

@Component({
  selector: 'add-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatProgressSpinner
  ],
  templateUrl: './add-user-dialog.component.html',
  styleUrl: './add-user-dialog.component.scss'
})
export class AddUserDialogComponent {
  private readonly dialogData = inject<AddUserDialogData>(MAT_DIALOG_DATA, { optional: true });
  isEditMode = !!this.dialogData?.user;

  addUserForm = new FormGroup({
    name: new FormControl(this.dialogData?.user?.name || '', [Validators.required]),
    email: new FormControl(this.dialogData?.user?.email || '', this.isEditMode ? [] : [Validators.required]),
    password: new FormControl('', this.isEditMode ? [] : [Validators.required]),
    country: new FormControl(this.dialogData?.user?.country || '', [Validators.required]),
    userId: new FormControl(''),
    updatePassAllowed: new FormControl<boolean | null>(this.isEditMode ? null : true),
    updatePassMandatory: new FormControl<boolean | null>(null),
  });

  constructor(private adminService: AdminService){
    if (this.isEditMode) this.addUserForm.controls.email.disable();
  }

  private readonly dialogRef = inject(MatDialogRef<AddUserDialogComponent>, { optional: true });

  isLoading: boolean = false;

  onAddUser(){
    if (!this.addUserForm.valid) return;
    const formValue = this.addUserForm.getRawValue();
    const json: PostUser = {
      userId: formValue.email || '',
      name: formValue.name || '',
      email: formValue.email || '',
      password: formValue.password || '',
      country: formValue.country || '',
      updatePassAllowed: !!formValue.updatePassAllowed,
    };
    this.isLoading = true;
    this.adminService.addUser(json).subscribe({
      next: (res: ServerResponseObject<User>)=>{
        this.isLoading = false;
        this.dialogRef?.close(true);
      },
      error: ()=>{
        this.isLoading = false;
      }
    });
  }

  onUpdateUser(){
    if (!this.addUserForm.valid || !this.dialogData?.user) return;
    const formValue = this.addUserForm.getRawValue();
    const user = this.dialogData.user;
    const json: UpdateUser = {
      userId: user.uuid,
      password: formValue.password?.trim() || null,
      name: formValue.name !== user.name ? formValue.name || null : null,
      country: formValue.country !== user.country ? formValue.country || null : null,
      updatePassAllowed: formValue.updatePassAllowed,
      updatePassMandatory: formValue.updatePassMandatory,
    };

    this.isLoading = true;
    this.adminService.updateUser(json).subscribe({
      next: (res: ServerResponseObject<User>)=>{
        this.isLoading = false;
        this.dialogRef?.close(true);
      },
      error: ()=>{
        this.isLoading = false;
      }
    });
  }
}
